"use client";

import { useRef, useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft, ArrowRight } from "lucide-react";
import parse from "html-react-parser";
import AnimateOnScroll from "./AnimateOnScroll";
import CustomButton from "./CustomButton";
import { ThemeName } from "./theme";
import { reactNodeToText } from "@/lib/utils";
import { SITE_URL } from "@/lib/metadata";

const ArticleCard = ({
  article,
  hideDesc = false,
}: {
  article: any;
  hideDesc?: boolean;
}) => (
  <article
    role="listitem"
    className="w-full shrink-0 rounded-[16px] bg-white/90 p-[10px] backdrop-blur-sm sm:w-[380px] lg:w-[calc((100%-40px)/3)]"
    aria-label={`Article: ${article.title}`}
  >
    {/* Inner white card */}
    <div className="flex h-[480px] flex-col overflow-hidden rounded-[10px] bg-white">
      {/* Article image */}
      <div className="relative h-[262px] w-full shrink-0">
        <Image
          src={article.featuredImage || "/images/v3/home/article-thumbnail.png"}
          alt=""
          fill
          className="object-cover"
          sizes="(max-width: 1024px) 380px, 500px"
          aria-hidden
        />
      </div>

      {/* Text content */}
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col">
          {/* Title */}
          <div className="px-[10px] py-[8.5px]">
            {(() => {
              const raw = article.title || "";
              const colonIdx = raw.indexOf(":");
              if (colonIdx !== -1) {
                const before = raw.slice(0, colonIdx).trim();
                const after = raw.slice(colonIdx + 1).trim();
                return (
                  <h3 className="font-crimsonText font-semibold text-black">
                    <span className="block text-[26px] leading-tight">
                      {parse(after)}
                    </span>
                  </h3>
                );
              }
              return (
                <h3 className="line-clamp-3 font-crimsonText text-[28px] font-semibold leading-tight text-black">
                  {parse(raw.slice(1, 50))}
                </h3>
              );
            })()}
          </div>

          {/* Description */}
          {!hideDesc && (
            <div className="px-[10px] py-[10px]">
              <p className="line-clamp-3 font-onest text-[15px] font-light leading-[140%] text-black/70">
                {article.excerpt || article.description}
              </p>
            </div>
          )}
        </div>

        {/* Read link */}
        <div className="px-[10px] pb-[10px]">
          <Link
            href={article.slug ? `/${article.slug}` : article.href || "#"}
            className="inline-flex items-center rounded-[8px] bg-black/10 px-[10px] py-[10px] font-onest text-[16px] font-light text-black transition-colors hover:bg-black/20"
            aria-label={`Read article: ${article.title}`}
          >
            Read article now
          </Link>
        </div>
      </div>
    </div>
  </article>
);

interface ArticlesProps {
  label?: string;
  heading?: React.ReactNode;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  theme?: ThemeName;
}

const Articles = ({
  label = "Articles by Alexander Rodriguez",
  heading = <>Guidance You Can Read Anytime</>,
  description = "Explore thoughtful articles on mental wellbeing, relationships, personal growth, grief, anxiety, confidence, and practical ways to move forward with clarity.",
  ctaLabel = "Articles",
  ctaHref = "/wellness-blog",
  theme = "blue",
}: ArticlesProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [articles, setArticles] = useState<any[]>([]);

  useEffect(() => {
    fetch("/api/posts?page=1&perPage=6")
      .then((res) => res.json())
      .then((data) => {
        if (data?.posts?.length > 0) setArticles(data.posts);
      })
      .catch((err) => console.error("Error loading articles:", err));
  }, []);

  const scroll = (dir: 1 | -1) => {
    if (!scrollRef.current) return;
    const firstCard = scrollRef.current.firstElementChild as HTMLElement;
    const cardW = firstCard ? firstCard.offsetWidth : 340;
    scrollRef.current.scrollBy({
      left: dir * (cardW + 20),
      behavior: "smooth",
    });
  };

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: reactNodeToText(heading),
    publisher: { "@type": "Person", name: "Alexander Rodriguez" },
    blogPost: articles.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      description: a.excerpt || a.description,
      ...(a.featuredImage
        ? { image: new URL(a.featuredImage, SITE_URL).href }
        : {}),
      url: new URL(a.slug ? `/${a.slug}` : a.href || "/", SITE_URL).href,
      author: {
        "@type": "Person",
        name: "Alex Rodriguez",
      },
      publisher: { "@type": "Person", name: "Alexander Rodriguez" },
    })),
  };

  return (
    <section
      className="container bg-[#F7FAFF]"
      aria-labelledby="articles-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(blogSchema).replace(/</g, "\\u003c"),
        }}
      />
      {/* Header */}
      <AnimateOnScroll className="mb-2 flex flex-col gap-4">
        <span
          data-from-left
          className="font-crimsonText text-[20px] font-bold italic text-v3-highlight lg:text-[24px]"
        >
          {label}
        </span>
        <h2 id="articles-heading" className="heading text-v3-secondary">
          {heading}
        </h2>
        <p className="max-w-[500px] font-onest text-[16px] font-normal leading-[1.5] text-black/60">
          {description}
        </p>
        <div>
          <CustomButton
            label={ctaLabel}
            href={ctaHref}
            variant="primary"
            withIcon
            theme={theme}
          />
        </div>
      </AnimateOnScroll>

      {/* Nav arrows + cards */}
      {articles.length > 0 && (
        <div className="flex flex-col gap-4">
          <div
            className="flex justify-end gap-3"
            role="group"
            aria-label="Article navigation"
          >
            <button
              onClick={() => scroll(-1)}
              className="flex size-[40px] items-center justify-center rounded-[14px] bg-black/10 text-black/60 transition-colors hover:bg-black/15 lg:size-[50px] lg:rounded-[16px] 3xl:size-[70px]"
              aria-label="Previous articles"
            >
              <ArrowLeft size={20} strokeWidth={1.5} />
            </button>
            <button
              onClick={() => scroll(1)}
              className="flex size-[40px] items-center justify-center rounded-[14px] bg-black/10 text-black/60 transition-colors hover:bg-black/15 lg:size-[50px] lg:rounded-[16px] 3xl:size-[70px]"
              aria-label="Next articles"
            >
              <ArrowRight size={20} strokeWidth={1.5} />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex w-full gap-5 overflow-x-auto scroll-smooth [scrollbar-width:none] lg:gap-8 3xl:gap-10 [&::-webkit-scrollbar]:hidden"
            role="list"
            aria-label="Articles"
          >
            {articles.map((a, idx) => (
              <ArticleCard key={a.id || idx} article={a} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default Articles;
